import { ColumnTable, DisplayInfo, Icon, Table } from '@octano/global-ui';
import dayjs from 'dayjs';
import { ReactElement, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { moneyFormatter } from '../../../../utils/currency';
import { AgreedPaymentRow } from '../types';

import { AgreedPaymentDetailModal } from './AgreedPaymentDetailModal';

const dictPrefix = 'financialPortal.agreedPayments.paymentsTable';

interface Props {
  data: AgreedPaymentRow[];
  hideStudentColumn?: boolean;
  isLoadingResults: boolean;
  totalPayments: number;
  totalPages: number;
  currentPage: number;
  hasFilters?: boolean;
  itemsPerPage: number;
  onChangePage: (page: number) => void;
}

function formatIdentification(
  value: string | null,
  country: string | null,
): string {
  if (!value) {
    return '-';
  }
  if (country) {
    return `${country}|${value}`;
  }
  return value;
}

export function AgreedPaymentsTable({
  data,
  hideStudentColumn = false,
  isLoadingResults,
  totalPayments,
  totalPages,
  currentPage,
  hasFilters,
  itemsPerPage,
  onChangePage,
}: Props): ReactElement {
  const { t } = useTranslation();
  const [detailDebtId, setDetailDebtId] = useState<string | null>(null);

  const handleViewDetail = useCallback(
    (debtId: string) => setDetailDebtId(debtId),
    [],
  );
  const handleCloseDetail = useCallback(() => setDetailDebtId(null), []);

  const columns = useMemo(() => {
    const cols: ColumnTable<AgreedPaymentRow>[] = [];
    if (!hideStudentColumn) {
      cols.push({
        headerText: t(`${dictPrefix}.student`),
        columnName: 'student',
        tdClassName: 'text-center',
        thClassName: 'text-center',
        cellFormat: ({ row }) => (
          <div>
            <span className="w-100 d-block text-center">
              {row.student.fullname}
            </span>
            <span className="w-100 d-block text-center">
              {formatIdentification(
                row.student.identification.value,
                row.student.identification.country,
              )}
            </span>
          </div>
        ),
      });
    }
    cols.push(
      {
        headerText: t(`${dictPrefix}.concept`),
        columnName: 'concept',
        tdClassName: 'text-center',
        thClassName: 'text-center',
        cellFormat: ({ row }) => `${row.concept} (${row.installment})`,
      },
      {
        headerText: t(`${dictPrefix}.debtId`),
        columnName: 'debtIdToDisplay',
        tdClassName: 'text-center',
        thClassName: 'text-center',
        cellFormat: ({ value }) => (value as string | null) ?? '-',
      },
      {
        headerText: t(`${dictPrefix}.year`),
        columnName: 'year',
        tdClassName: 'text-center',
        thClassName: 'text-center',
        cellFormat: ({ value }) => (value as number | null) ?? '-',
      },
      {
        headerText: t(`${dictPrefix}.contract`),
        columnName: 'contract',
        tdClassName: 'text-center',
        thClassName: 'text-center',
        cellFormat: ({ value }) => (value as string | null) ?? '-',
      },
      {
        headerText: t(`${dictPrefix}.initialAmount`),
        columnName: 'initialAmount',
        tdClassName: 'text-right',
        thClassName: 'text-right',
        cellFormat: ({ value }) =>
          moneyFormatter().format(Math.ceil(Number(value ?? 0))),
      },
      {
        headerText: t(`${dictPrefix}.currentDebt`),
        columnName: 'currentDebt',
        tdClassName: 'text-right',
        thClassName: 'text-right',
        cellFormat: ({ value }) => (
          <strong>
            {moneyFormatter().format(Math.ceil(Number(value ?? 0)))}
          </strong>
        ),
      },
      {
        headerText: t(`${dictPrefix}.dueDate`),
        columnName: 'installmentDueDate',
        tdClassName: 'text-center',
        thClassName: 'text-center',
        cellFormat: ({ value }) =>
          value
            ? dayjs(value as string)
                .utc()
                .format('DD/MM/YYYY')
            : '-',
      },
      {
        headerText: t(`${dictPrefix}.action`),
        columnName: 'id',
        tdClassName: 'text-center',
        thClassName: 'text-center',
        cellFormat: ({ row }) => (
          <button
            type="button"
            className="border-0 bg-transparent p-0"
            aria-label={t(`${dictPrefix}.viewDetail`)}
            onClick={() => handleViewDetail(row.id)}
          >
            <Icon name="eye" />
          </button>
        ),
      },
    );
    return cols;
  }, [hideStudentColumn, handleViewDetail, t]);

  return (
    <>
      <Table
        isLoadingResults={isLoadingResults}
        data={data}
        columns={columns}
        pagination={{
          currentPage: currentPage + 1,
          itemsPerPage,
          totalItems: totalPayments,
          totalPages,
          onChangePage: (page) => onChangePage(page - 1),
        }}
        noResultsText={
          <div className="d-flex justify-content-center align-items-center py-5">
            <DisplayInfo
              iconName="information"
              textBody={t(
                `${dictPrefix}.${
                  hasFilters ? 'noResultsFilter' : 'noResults'
                }.description`,
              )}
              maxWidth="650px"
            />
          </div>
        }
      />
      <AgreedPaymentDetailModal
        debtId={detailDebtId}
        isOpen={detailDebtId !== null}
        onClose={handleCloseDetail}
      />
    </>
  );
}
